/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Button,
    Flex,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {MESSAGE_PROPTYPE} from '@salesforce/retail-react-app/app/utils/locale.js'
import {remCalc} from '../../theme/_utils'

/**
 * Renders a form submit button and a cancel button with configurable labels and callbacks
 * in a responsive layout. Used primarily in forms that can be toggled opened/closed.
 */
const FormActionButtons = ({
    saveButtonProps = {},
    cancelButtonProps = {},
    saveButtonLabel,
    cancelButtonLabel,
    isDisabled = false,
    onCancel = () => {},
    onSubmit = () => {},
    variant = 'default',
}) => {
    const styles = useMultiStyleConfig('Account', {variant})
    return (
        <Flex gap={remCalc(24)} {...styles.formActionButtons}>
            <Button
                variant="secondary"
                {...cancelButtonProps}
                onClick={() => onCancel()}
                w={{base: '100%', md: '50%'}}
                {...styles.formActionButton}
                isDisabled={isDisabled}
            >
                {cancelButtonLabel ? (
                    <FormattedMessage {...cancelButtonLabel} />
                ) : (
                    <FormattedMessage
                        id="form_action_buttons.button.cancel"
                        defaultMessage="Cancel"
                    />
                )}
            </Button>
            <Button
                variant="primary"
                onClick={() => onSubmit()}
                w={{base: '100%', md: '50%'}}
                {...saveButtonProps}
                isDisabled={isDisabled}
            >
                {saveButtonLabel ? (
                    <FormattedMessage {...saveButtonLabel} />
                ) : (
                    <FormattedMessage defaultMessage="Save" id="form_action_buttons.button.save" />
                )}
            </Button>
        </Flex>
    )
}

FormActionButtons.propTypes = {
    saveButtonProps: PropTypes.object,
    cancelButtonProps: PropTypes.object,
    saveButtonLabel: MESSAGE_PROPTYPE,
    cancelButtonLabel: MESSAGE_PROPTYPE,
    onCancel: PropTypes.func,
    onSubmit: PropTypes.func,
    variant: PropTypes.string,
    isDisabled: PropTypes.bool,
}

export default FormActionButtons
